import React from 'react';
import { func, string } from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Map as MapIcon, MessageCircle } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getCityLandingConfig } from '../../util/cityLandingPageConfig';
import NamedLink from '../NamedLink/NamedLink';

import css from './FloatingMapButton.module.css';

// Switzerland viewport: NE_lat,NE_lng,SW_lat,SW_lng
const SWITZERLAND_BOUNDS = '47.808455,10.491944,45.817995,5.955900';

const CITY_PATH = /^\/coworking\/([^/?#]+)(\/|$|\?)/;

/**
 * The viewport the map opens on. On a city page that is the city's own box — until 2026-09-22 the
 * button always opened Switzerland, so the one control that stays on screen while scrolling
 * through Zurich listings led out of Zurich. Anything else falls back to the country.
 *
 * @param {string} pathname
 * @returns {string} bounds string for the SearchPage `bounds` param
 */
export const boundsForPath = pathname => {
  const slug = (pathname.match(CITY_PATH) || [])[1];
  return getCityLandingConfig(slug)?.boundsStr || SWITZERLAND_BOUNDS;
};

/**
 * The control that stays on screen while the visitor scrolls the results: the map, and — when the
 * page hands over a `requestHref` — the direct request next to it, so asking is one tap away from
 * any card.
 *
 * Rendered by the page that wants it (SearchView, the lounge hub), not globally: the page owns the
 * request form the second action jumps to.
 *
 * @param {Object} props
 * @param {string?} props.bounds the viewport the map opens on; the page that renders the button
 *   knows its city, the URL is only the fallback
 * @param {string?} props.requestHref anchor of the page's request form (e.g. `#anfrage`)
 * @param {Function?} props.onRequestClick called on a click on the request action
 * @param {Function?} props.onMapClick when given, a click opens the page's own map instead of
 *   following the link to the search page (the href stays for crawlers and the case without JS)
 * @param {string?} props.trackPrefix prefix of the `data-track-cta` ids (`city`, `search`)
 */
const FloatingMapButton = props => {
  const { bounds, requestHref, onRequestClick, onMapClick, trackPrefix = 'city' } = props;
  const location = useLocation();
  const intl = useIntl();

  const search = `?bounds=${bounds || boundsForPath(location?.pathname || '')}&mapSearch=true`;
  const mapAriaLabel = intl.formatMessage({ id: 'FloatingMapButton.aria' });

  const mapContent = (
    <>
      <MapIcon className={css.icon} aria-hidden="true" strokeWidth={2.25} />
      <span className={css.label}>
        <FormattedMessage id="FloatingMapButton.label" />
      </span>
    </>
  );

  if (!requestHref) {
    return (
      <NamedLink
        name="SearchPage"
        to={{ search }}
        className={css.root}
        ariaLabel={mapAriaLabel}
        title={mapAriaLabel}
        onClick={onMapClick}
      >
        {mapContent}
      </NamedLink>
    );
  }

  const requestAriaLabel = intl.formatMessage({ id: 'FloatingMapButton.requestAria' });
  return (
    <div className={css.bar}>
      <NamedLink
        name="SearchPage"
        to={{ search }}
        className={css.segment}
        ariaLabel={mapAriaLabel}
        title={mapAriaLabel}
        dataTrackCta={`${trackPrefix}_map`}
        onClick={onMapClick}
      >
        {mapContent}
      </NamedLink>
      <span className={css.divider} aria-hidden="true" />
      <a
        href={requestHref}
        className={css.segment}
        aria-label={requestAriaLabel}
        title={requestAriaLabel}
        data-track-cta={`${trackPrefix}_request_float`}
        onClick={onRequestClick}
      >
        <MessageCircle className={css.icon} aria-hidden="true" strokeWidth={2.25} />
        <span className={css.label}>
          <FormattedMessage id="FloatingMapButton.requestLabel" />
        </span>
      </a>
    </div>
  );
};

FloatingMapButton.propTypes = {
  bounds: string,
  requestHref: string,
  onRequestClick: func,
  onMapClick: func,
  trackPrefix: string,
};

export default FloatingMapButton;
